import { AppModels } from './components/app-models';
import { AppProviders } from './components/app-providers';
import { useAnalytics } from './hooks/use-analyze';
import { BlockLoading } from '@/client/components/fetch-status/block-loading';
import { RouteTransitionIndicator } from '@/client/components/route-transition-indicator';
import { useInitApp } from '@/client/hooks/use-init-app';
import { useTransitionLocation } from '@/client/hooks/use-transition-location';
import { AppRouter } from '@/client/router';
import { Component, memo, Suspense } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Router } from 'wouter';

class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App crash caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px' }}>
          <p style={{ fontSize: '16px', color: '#666' }}>Something went wrong</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '8px 24px', borderRadius: '8px', border: '1px solid #ddd', cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AppInitializer = memo(() => {
  useInitApp();
  useAnalytics();
  return null;
});

AppInitializer.displayName = 'AppInitializer';

const App = () => {
  return (
    <AppErrorBoundary>
      <Suspense fallback={<BlockLoading />}>
        <AppProviders>
          {/* Single Router mount: every wouter consumer in the tree reads the
              transition-aware location hook, so path updates defer while lazy
              route chunks load (useSearch stays urgent by design). */}
          <Router hook={useTransitionLocation}>
            <AppInitializer />
            <RouteTransitionIndicator />
            <AppRouter />
            <AppModels />
          </Router>
        </AppProviders>
      </Suspense>
    </AppErrorBoundary>
  );
};

export default App;
